import { get, post } from 'axios'
import { v4 } from 'uuid'
import { lib } from '@gocanvas/client'
const { getValue, getOperationType, conditionCheck } = lib

const getToken = () => document.querySelector('[name="csrf-token"]').content
const buildHeaders = () => ({
  headers: {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getToken(),
  },
})

export const csrfFetch = url => get(url, buildHeaders())

export const fetchConditionTypes = () => csrfFetch('/condition_types.json')

export const getForm = async form_id => {
  const formEndpoint = `/api/v3/forms/${form_id}?format=flat_client`

  return get(formEndpoint)
}

export const getWebForm = async form_id => {
  const formEndpoint = `/api/v3/forms/${form_id}?format=web_form`

  return get(formEndpoint)
}

export const getReferenceData = async form_id => {
  const refDataEndpoint = `/reference_datas.json?form_id=${form_id}`

  return get(refDataEndpoint)
}

export const fetchFormData = async formId => {
  const { initialOnboardingState = {} } = window || {}
  const { user = {} } = initialOnboardingState
  const { appId } = user
  const id = formId || appId
  if (id) {
    const { data: formData } = await getForm(id)
    const { data: conditionTypes } = await fetchConditionTypes()
    const { data: refData } = await getReferenceData(id)
    const { entities } = unpackFlatApp(formData)
    return {
      ...entities,
      conditionTypes: conditionTypes.map(unpackConditionType),
      refData,
    }
  }
}

export const fetchWebFormData = async () => {
  const { initialOnboardingState = {} } = window || {}
  const { appId, webFormLogo } = initialOnboardingState

  if (appId) {
    const { data: formData } = await getWebForm(appId)
    const { data: conditionTypes } = await fetchConditionTypes()
    const { entities } = unpackFlatApp(formData)
    const { app } = entities
    return {
      entities: {
        ...entities,
        app: {
          ...app,
          logo: webFormLogo,
        },
      },
      types: {
        conditionTypes: conditionTypes.map(unpackConditionType),
      },
    }
  }
}

const linkConditions = (conditions, entries) => {
  return conditions.map(({ entry_id, parent_entry_id, ...condition }) => {
    const parentEntry = entries.find(({ id }) => id === parent_entry_id) || {}
    return {
      parentEntryGuid: parentEntry.guid,
      ...condition,
    }
  })
}

const linkOperations = operations => {
  return operations.map(({ operation_type_id, ...operation }, i) => {
    return {
      operation_type_id,
      operation_type: getOperationType(operation_type_id),
      index: i,
      ...operation,
    }
  })
}

const linkMultiSection = sheets =>
  sheets.map(({ multi_section, ...sheet }) => ({
    ...sheet,
    multi_section: multi_section ? multi_section.id : multi_section,
  }))

const normalizeEntryTypes = entry => {
  const type = entry.type == 'Summary' ? 'Sum' : entry.type
  return {
    ...entry,
    type
  }
}

export const unpackFlatApp = ({
  operations,
  conditions,
  entries,
  sheets,
  sections,
  ...app
}) => {
  const normalizedEntries = entries.map(normalizeEntryTypes)
  return {
    entities: {
      operations: linkOperations(operations),
      conditions: linkConditions(conditions, normalizedEntries),
      entries: normalizedEntries,
      sheets: linkMultiSection(sheets),
      sections,
      app,
    },
  }
}

export const unpackConditionType = ({ condition_type }) => condition_type

const fieldTypeTransforms = entry => {
  const { type, value, length, ...rest } = entry

  // image fields should create a content_guid, later used as a reference
  if (type === 'Signature' && value) {
    return {
      type,
      ...rest,
      value,
      content_guid: v4(),
    }
  }

  if (type === 'Photo') {
    if (value && value.length) {
      if (length > 1) {
        return {
          type,
          ...rest,
          content_guids: value.map(({ url }) => ({ url, content_guid: v4() })),
        }
      } else {
        return {
          type,
          ...rest,
          value: value[0].url,
          content_guid: v4(),
        }
      }
    }
  }

  return {
    ...entry,
    value: getValue(type, value),
  }
}

const fetchBlobAppend = (formData, content_guid, value) =>
  fetch(value)
    .then(response => response.blob())
    .then(file => {
      formData.append(content_guid, file)
    })

// extracts any files and appends them to the form
async function extractFiles(formData, transformedData) {
  await Promise.all(
    transformedData
      .filter(
        ({ content_guid, content_guids }) => content_guid || content_guids
      )
      .flatMap(({ content_guid, content_guids, value }) => {
        if (content_guid) {
          return [fetchBlobAppend(formData, content_guid, value)]
        } else if (content_guids && content_guids.length) {
          return content_guids.map(({ url, content_guid }) =>
            fetchBlobAppend(formData, content_guid, url)
          )
        }
      })
  )
  return formData
}

// entries with content_guid should not have values, and vice-versa
const filterFieldProps = ({
  id,
  value,
  displayed,
  content_guid,
  content_guids,
  multi_key,
}) => {
  const field = {
    entry_id: id,
    displayed,
    multi_key,
  }
  if (content_guid) {
    return { ...field, content_guid }
  } else if (content_guids) {
    return {
      ...field,
      content_guids: content_guids.map(({ content_guid }) => content_guid),
    }
  }
  return { ...field, value }
}

export const annotateValidPreviewEntries = ({ previewEntries, validSheetIds, conditions }) =>
  previewEntries.map(entry => {
    const { sheet_id, rowGuid, parentGuid, guid } = entry
    const displayed =
      validSheetIds.includes(sheet_id) &&
      conditionCheck(parentGuid || guid, previewEntries, conditions, rowGuid || false)
    return { ...entry, displayed }
  })


export const assembleSubmission = async (
  { formVersion, formId, guid },
  data
) => {
  const formData = new FormData()

  formData.append('guid', v4())
  formData.append(
    'form',
    JSON.stringify({
      id: formId,
      guid,
      version: formVersion,
      type: 'form',
    })
  )

  const associateLoops = data => {
    return data.map(
      ({ parentGuid, keyfieldGuid, rowGuid, keyField, ...entry }) => {
        if (parentGuid && !keyField) {
          const parentEntry = data.find(
            parentEntry =>
              parentEntry.rowGuid === rowGuid &&
              parentEntry.keyfieldGuid == keyfieldGuid
          )
          return fieldTypeTransforms({
            multi_key: parentEntry.value,
            ...entry,
          })
        }

        return fieldTypeTransforms(entry)
      }
    )
  }

  const transformedData = associateLoops(data)

  formData.append(
    'responses',
    JSON.stringify(transformedData.map(filterFieldProps))
  )

  return await extractFiles(formData, transformedData)
}

export const createAPISubmission = async ({
  formId,
  formVersion,
  previewEntries,
}) => {
  const submissionEndpoint = '/api/v3/submissions'
  const formMeta = {
    formId,
    formVersion,
  }

  const submissionData = await assembleSubmission(formMeta, previewEntries)
  return post(submissionEndpoint, submissionData)
}

// TODO: not used, remove?
export const createWebFormSubmission = async ({
  formId,
  formVersion,
  previewEntries,
}) => {
  const submissionEndpoint = '/api/v3/submissions/web_forms'
  const formMeta = {
    guid: formId,
    formVersion,
  }

  const submissionData = await assembleSubmission(formMeta, previewEntries)
  return post(submissionEndpoint, submissionData)
}
